import { createMachine, state, transition, guard } from 'robot3';
import { RESOURCE_CONFIG, THRESHOLD_CONFIG, BASE_CONFIG } from '../config.js';

// Guard functions for state machine conditions
// Robot3 passes the event object as the context, so we need to extract our data from it
export const guards = {
  // Base-related guards
  hasBase: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.alive;
  },
  baseNeedsRepair: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.health < unit.base.maxHealth;
  },
  isAtBaseCenter: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.isAtCenter && unit.base.isAtCenter(unit);
  },
  
  // Ammo and health guards
  hasAmmo: (context) => {
    const { unit } = context;
    return unit && unit.ammo > 0;
  },
  needsAmmo: (context) => {
    const { unit } = context;
    return unit && unit.ammo <= 0;
  },
  hasEnoughAmmo: (context) => {
    const { unit } = context;
    return unit && unit.ammo >= unit.maxAmmo * 0.7;
  },
  lowHealth: (context) => {
    const { unit } = context;
    return unit && unit.health < unit.maxHealth * 0.3; // FLEE_HEALTH_THRESHOLD
  },
  healthyEnough: (context) => {
    const { unit } = context;
    return unit && unit.health >= unit.maxHealth * 0.8;
  },
  
  // Resource guards
  carryingMetal: (context) => {
    const { unit } = context;
    return unit && unit.carryingResource && unit.carryingResource.type === 'metal';
  },
  canGatherMetal: (context) => {
    const { unit } = context;
    if (!unit) return false;

    // Can't gather if cargo is full
    if (unit.getCargoCount && unit.getCargoCount() >= BASE_CONFIG.SOLDIER_CARGO_CAPACITY) return false;

    // Normal rule: only units below level 5
    if (unit.level < 5) return true;
    
    // Exception: Allow level 5+ if base is critical and no low-level units exist
    if (unit.base && unit.base.alive) {
      const baseCritical = unit.base.ammo < unit.base.maxAmmo * 0.2; // Less than 20% ammo
      if (baseCritical) {
        // Need access to all soldiers - check through AI context
        const { ai } = context;
        if (ai && ai.nearbyAllies) {
          const lowLevelTeammates = ai.nearbyAllies.filter(s => 
            s.alive && s.level < 5
          );
          
          // If no low-level teammates, allow high-level gathering
          return lowLevelTeammates.length === 0;
        }
        
        // If we can't check teammates, allow gathering in critical state
        return true;
      }
    }
    
    return false;
  },
  canGatherNebula: (context) => {
    const { unit, ai } = context;
    if (!unit || unit.carryingResource) return false;
    if (unit.level >= 5) return false; // Only units below level 5 can gather nebula

    // Trust base's superior perception - if base has assigned a nebula target, accept it
    // Bases have 160px perception vs soldier's 80px, so they can see distant nebulas
    if (unit.base && unit.base.priority === 'get_organics' &&
        unit.base.targetResource && unit.base.targetResource.active) {
      return true; // Base has already validated this nebula
    }

    // Otherwise check soldier's own perception for opportunistic gathering
    if (!ai || !ai.nearbyResources) return false;
    const nearbyNebulas = ai.nearbyResources.filter(r => r.type === 'nebula' && r.active);
    return nearbyNebulas.length > 0;
  },
  nebulaHasSlot: (context) => {
    const { ai } = context;
    if (!ai || !ai.targetNebula) return false;
    const nebula = ai.targetNebula;
    return nebula.gatherers.size < RESOURCE_CONFIG.NEBULA_MAX_GATHERERS;
  },
  withinNebulaRange: (context) => {
    const { unit, ai } = context;
    if (!ai || !ai.targetNebula) return false;
    return unit.getDistance(ai.targetNebula) <= RESOURCE_CONFIG.NEBULA_RADIUS;
  },
  
  // Combat guards
  hasEnemy: (context) => {
    const { ai } = context;
    return ai && ai.currentTarget && ai.isValidTarget(ai.currentTarget);
  },
  shouldRetreat: (context) => {
    const { ai } = context;
    return ai && ai.shouldRetreatFromCombat();
  },
  courageRestored: (context) => {
    const { unit } = context;
    return unit && unit.courage >= unit.maxCourage * 0.3;
  },
  
  // Base priority guards
  basePriorityDefend: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.priority === 'defend';
  },
  basePriorityGetMetal: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.priority === 'get_metal';
  },
  basePriorityGetNearbyMetal: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.priority === 'get_nearby_metal';
  },
  
  basePriorityGetOrganics: (context) => {
    const { unit } = context;
    return unit && unit.base && unit.base.alive && unit.base.priority === 'get_organics';
  },

  // Assist coordination
  hasAssistObjective: (context) => {
    const { ai } = context;
    return ai && !!ai.assistObjective && ai.assistObjective.type === 'assist';
  },
  canAssistAlly: (context) => {
    const { unit, ai } = context;
    if (!ai || !ai.assistObjective || !unit) return false;
    if (ai.assistObjective.type !== 'assist') return false;
    if (unit.state === 'returning_resource' && unit.carryingResource) return false;
    if (unit.health < unit.maxHealth * THRESHOLD_CONFIG.RADIO_MIN_HEALTH_RATIO) return false;
    if (unit.ammo < unit.maxAmmo * THRESHOLD_CONFIG.RADIO_MIN_AMMO_RATIO) return false;
    return true;
  },

  // Defense coordination
  hasDefenseObjective: (context) => {
    const { ai } = context;
    return ai && !!ai.assistObjective && ai.assistObjective.type === 'defend_base';
  },
  canDefendBase: (context) => {
    const { unit, ai } = context;
    if (!ai || !ai.assistObjective || !unit) return false;
    if (ai.assistObjective.type !== 'defend_base') return false;
    // More lenient requirements for base defense - even low ammo/health soldiers will help
    return true;
  },

  // Special conditions
  noBaseExists: (context) => {
    const { unit } = context;
    return unit && (!unit.base || !unit.base.alive);
  },
  retreatCooldownOver: (context) => {
    const { ai } = context;
    return ai && ai.retreatCooldown <= 0;
  }
};

// Events that can trigger state transitions
export const events = {
  // Combat events
  AMMO_EMPTY: 'AMMO_EMPTY',
  AMMO_SUFFICIENT: 'AMMO_SUFFICIENT', 
  ENEMY_FOUND: 'ENEMY_FOUND',
  ENEMY_DEFEATED: 'ENEMY_DEFEATED',
  SHOULD_RETREAT: 'SHOULD_RETREAT',
  RETREAT_COMPLETE: 'RETREAT_COMPLETE',
  
  // Resource events
  METAL_FOUND: 'METAL_FOUND',
  CRYSTAL_FOUND: 'CRYSTAL_FOUND',
  NEBULA_FOUND: 'NEBULA_FOUND',
  CORPSE_FOUND: 'CORPSE_FOUND',
  RESOURCE_COLLECTED: 'RESOURCE_COLLECTED',
  RESOURCE_DELIVERED: 'RESOURCE_DELIVERED',
  NEBULA_SLOT_CLAIMED: 'NEBULA_SLOT_CLAIMED',
  NEBULA_DEPLETED: 'NEBULA_DEPLETED',
  NEBULA_INTERRUPTED: 'NEBULA_INTERRUPTED',
  CARGO_FULL: 'CARGO_FULL',
  
  // Health events
  LOW_HEALTH: 'LOW_HEALTH',
  HEALTH_RESTORED: 'HEALTH_RESTORED',
  
  // Base events
  BASE_PRIORITY_DEFEND: 'BASE_PRIORITY_DEFEND',
  BASE_PRIORITY_GET_METAL: 'BASE_PRIORITY_GET_METAL',
  BASE_DESTROYED: 'BASE_DESTROYED',
  REACHED_BASE: 'REACHED_BASE',

  // Condition checks
  CHECK_CONDITIONS: 'CHECK_CONDITIONS',

  // Radio assist events
  ALLY_ASSIST_REQUEST: 'ALLY_ASSIST_REQUEST',
  ASSIST_TARGET_REACHED: 'ASSIST_TARGET_REACHED',
  ASSIST_CANCELLED: 'ASSIST_CANCELLED',

  // Radio defense events
  BASE_DEFENSE_REQUEST: 'BASE_DEFENSE_REQUEST',
  DEFENSE_TARGET_REACHED: 'DEFENSE_TARGET_REACHED',
  DEFENSE_CANCELLED: 'DEFENSE_CANCELLED'
};

// Create the soldier state machine
export const createSoldierStateMachine = (initialState = 'idle') => {
  return createMachine(initialState, {
    // Initial idle state - units start here and transition to active states
    idle: state(
      // Immediate transitions to active states
      transition(events.CHECK_CONDITIONS, 'hunting'),
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.METAL_FOUND, 'gathering', guard(guards.canGatherMetal)),
      transition(events.BASE_PRIORITY_DEFEND, 'defending'),
      transition(events.BASE_DEFENSE_REQUEST, 'defending_base', guard(guards.canDefendBase)),
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly))
    ),
    
    // Primary hunting state
    hunting: state(
      // Corpses are highest priority (free level-up!)
      transition(events.CORPSE_FOUND, 'collecting_corpse'),

      // Critical priorities (ammo and health)
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),
      transition(events.LOW_HEALTH, 'fleeing'),

      // Base priorities
      transition(events.BASE_PRIORITY_DEFEND, 'defending'),
      transition(events.BASE_DEFENSE_REQUEST, 'defending_base', guard(guards.canDefendBase)),
      transition(events.BASE_PRIORITY_GET_METAL, 'gathering', guard(guards.canGatherMetal)),

      // Resource priorities
      transition(events.NEBULA_FOUND, 'gathering_nebula', guard(guards.canGatherNebula)),

      // Combat and resources
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly)),
      transition(events.METAL_FOUND, 'gathering', guard(guards.canGatherMetal)),
      transition(events.CRYSTAL_FOUND, 'gathering'),
        transition(events.CARGO_FULL, 'returning_resource'),

        // Base destruction
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),
    
    // Combat state
    fighting: state(
      // Corpse found after kill - only collect if not already carrying resources
      transition(events.CORPSE_FOUND, 'collecting_corpse', guard((ctx) => {
        const { unit } = ctx;
        return unit && !unit.carryingResource;
      })),

      // Critical overrides
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),
      transition(events.LOW_HEALTH, 'fleeing'),

      // Combat outcomes
      transition(events.SHOULD_RETREAT, 'retreating'),
      transition(events.NEBULA_FOUND, 'gathering_nebula'), // Return to assigned nebula
      // If carrying resource, resume delivery after combat
      transition(events.RESOURCE_COLLECTED, 'returning_resource'), // Handler sends this when carrying resource
      transition(events.CORPSE_FOUND, 'returning_resource', guard(guards.carryingMetal)),
      transition(events.ENEMY_DEFEATED, 'returning_resource', guard(guards.carryingMetal)),
      transition(events.ENEMY_DEFEATED, 'hunting'),

      // Base destruction
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),
    
    // Resource gathering
    gathering: state(
      // Critical overrides
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),

      // Base defense interrupts gathering
      transition(events.BASE_DEFENSE_REQUEST, 'defending_base', guard(guards.canDefendBase)),

      // Gathering outcomes
      transition(events.RESOURCE_COLLECTED, 'returning_resource'),
      transition(events.CHECK_CONDITIONS, 'hunting'), // Return to hunting when no resources or cargo not full
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly)),

      // Base destruction - drop resource and enter final hunt
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),
    
    // Nebula gathering (cooperative organics collection)
    gathering_nebula: state(
      // Critical overrides - must release nebula slot
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),
      transition(events.LOW_HEALTH, 'fleeing'),
      
      // Combat interruption - release slot and fight
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly)),

      // Nebula gathering outcomes
      transition(events.NEBULA_SLOT_CLAIMED, 'gathering_nebula'), // Stay in state but claimed slot
      transition(events.RESOURCE_COLLECTED, 'returning_resource'), // Got enough organics to deliver
      transition(events.NEBULA_DEPLETED, 'hunting'),
      transition(events.NEBULA_INTERRUPTED, 'hunting'),
      
      // Base destruction
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),
    
    // Collecting corpse (highest priority resource)
    collecting_corpse: state(
      // Once collected, check if carrying resources
      transition(events.RESOURCE_COLLECTED, 'returning_resource', guard((ctx) => {
        const { unit } = ctx;
        return unit && unit.carryingResource && unit.base && unit.base.alive;
      })),
      transition(events.RESOURCE_COLLECTED, 'hunting'),
      
      // No corpse available - return to hunting
      transition(events.CHECK_CONDITIONS, 'hunting'),
      
      // Can be interrupted by critical needs
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      
      // Combat interruption only if very close
      transition(events.ENEMY_FOUND, 'fighting'),
      
      // Base destruction
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),
    
    // Returning resources to base
    returning_resource: state(
      // Safety check - if no resource, return to hunting
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => {
        const { unit } = ctx;
        return unit && !unit.carryingResource;
      })),

      // Combat interruption - fight while keeping resource, then resume delivery
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly)),

      // Ammo takes precedence over resource delivery
      transition(events.AMMO_EMPTY, 'reloading'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),

      // Resource delivery outcomes
      transition(events.RESOURCE_DELIVERED, 'reloading', guard(guards.baseNeedsRepair)),
      transition(events.RESOURCE_DELIVERED, 'hunting'),
      transition(events.BASE_DESTROYED, 'fleeing')
    ),
    
    // Reloading at base
    reloading: state(
      // Corpses provide instant full ammo + level up - better than base resupply!
      transition(events.CORPSE_FOUND, 'collecting_corpse'),
      
      transition(events.REACHED_BASE, 'resupplying'),
      transition(events.HEALTH_RESTORED, 'hunting', guard(guards.hasEnoughAmmo)),
      transition(events.BASE_DESTROYED, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing')
    ),
    
    // Withdrawing to base (returning to heal)
    withdrawing: state(
      // Corpses provide instant full health + ammo + level up - better than base!
      transition(events.CORPSE_FOUND, 'collecting_corpse'),
      
      transition(events.REACHED_BASE, 'resupplying'),
      transition(events.HEALTH_RESTORED, 'hunting', guard(guards.hasEnoughAmmo)),
      transition(events.HEALTH_RESTORED, 'reloading'),
      transition(events.BASE_DESTROYED, 'fleeing')
    ),

    // Defending base
    defending: state(
      // Critical overrides
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),

      // Defense outcomes
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => !guards.basePriorityDefend(ctx))),
      transition(events.ALLY_ASSIST_REQUEST, 'assisting', guard(guards.canAssistAlly)),

      // Base destruction
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),

    // Responding to ally assist request
    assisting: state(
      transition(events.AMMO_EMPTY, 'reloading', guard(guards.hasBase)),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'withdrawing', guard(guards.hasBase)),
      transition(events.LOW_HEALTH, 'fleeing'),
      transition(events.ASSIST_TARGET_REACHED, 'fighting'),
      transition(events.ASSIST_CANCELLED, 'hunting', guard((ctx) => !guards.hasAssistObjective(ctx))),
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => !guards.hasAssistObjective(ctx))),
      transition(events.BASE_DESTROYED, 'final_hunt')
    ),

    // Responding to base defense request
    defending_base: state(
      // Defense is high priority - even wounded/low ammo soldiers can patrol
      transition(events.AMMO_EMPTY, 'defending_base'), // Stay and defend even without ammo
      transition(events.LOW_HEALTH, 'defending_base'), // Stay and defend even wounded

      // Combat and defense outcomes
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.DEFENSE_TARGET_REACHED, 'defending_base'), // Arrived, now patrol
      transition(events.DEFENSE_CANCELLED, 'hunting', guard((ctx) => !guards.hasDefenseObjective(ctx))),
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => !guards.hasDefenseObjective(ctx))),

      // Only leave for critical base needs
      transition(events.REACHED_BASE, 'resupplying', guard((ctx) => {
        const { unit } = ctx;
        return unit && unit.health < unit.maxHealth * 0.2; // Only if critically wounded
      })),

      transition(events.BASE_DESTROYED, 'final_hunt')
    ),

    // Retreating from combat
    retreating: state(
      transition(events.RETREAT_COMPLETE, 'hunting', guard(guards.courageRestored)),
      transition(events.REACHED_BASE, 'reloading'),
      transition(events.BASE_DESTROYED, 'fleeing')
    ),
    
    // Fleeing (no base or desperate)
    fleeing: state(
      // Corpses can save fleeing soldiers!
      transition(events.CORPSE_FOUND, 'collecting_corpse'),
      
      transition(events.REACHED_BASE, 'resupplying'),
      transition(events.HEALTH_RESTORED, 'final_hunt', guard(guards.noBaseExists)),
      transition(events.HEALTH_RESTORED, 'hunting', guard(guards.hasBase)),
      transition(events.CHECK_CONDITIONS, 'final_hunt', guard((ctx) => guards.noBaseExists(ctx) && guards.courageRestored(ctx))),
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => guards.hasBase(ctx) && guards.courageRestored(ctx))),
      transition(events.ENEMY_FOUND, 'fighting', guard((ctx) => guards.hasAmmo(ctx) && guards.courageRestored(ctx)))
    ),
    
    // Resupplying at base - unified state for health, courage, and ammo restoration
    resupplying: state(
      // Exit conditions - when fully resupplied
      transition(events.CHECK_CONDITIONS, 'hunting', guard((ctx) => {
        const { unit } = ctx;
        return unit && unit.health >= unit.maxHealth * 0.8 && 
               unit.courage >= unit.maxCourage * 0.8 && 
               unit.ammo >= unit.maxAmmo * 0.7;
      })),
      
      // Base defense takes priority even while resupplying
      transition(events.BASE_PRIORITY_DEFEND, 'defending'),
      
      // Base destruction
      transition(events.BASE_DESTROYED, 'fleeing')
    ),
    
    // Final hunt mode (no base, last stand)
    final_hunt: state(
      transition(events.ENEMY_FOUND, 'fighting'),
      transition(events.AMMO_EMPTY, 'fleeing'),
      transition(events.LOW_HEALTH, 'fleeing')
    )
  });
};

// Helper function to create state machine context
export const createContext = (unit, ai) => ({
  unit,
  ai,
  timestamp: Date.now()
});